import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Trash2, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import ProposalCardShell from './ProposalCardShell';
import { routinesApi } from '@/services/routines-api';
import { handleApiError } from '@/services/api';
import { humanReadableCron } from '@/utils/cron';
import type { ToolSegmentPayload } from '@/types/chat';

type Proposal = Extract<ToolSegmentPayload, { type: 'routine_deletion_proposal' }>;

interface Props {
  proposal: Proposal;
}

const RoutineDeletionProposalCard: React.FC<Props> = ({ proposal }) => {
  const [dismissed, setDismissed] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const current = proposal.current as {
    name?: string;
    schedule?: string;
    timezone?: string;
    message?: string;
  };

  const deleteMutation = useMutation({
    mutationFn: () => routinesApi.deleteRoutine(proposal.routine_id),
    onSuccess: () => {
      setDeleted(true);
      toast.success('Routine deleted');
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to delete routine');
    },
  });

  if (dismissed) {
    return (
      <ProposalCardShell
        kicker="Routine deletion"
        title={current.name ?? 'Routine'}
        actions={null}
        accentClass="bg-destructive"
        resolved={{ tone: 'muted', label: 'Kept — proposal dismissed' }}
      >
        <div />
      </ProposalCardShell>
    );
  }

  if (deleted) {
    return (
      <ProposalCardShell
        kicker="Routine deletion"
        title={current.name ?? 'Routine'}
        actions={null}
        accentClass="bg-destructive"
        resolved={{ tone: 'danger', label: '✓ Routine deleted' }}
      >
        <div />
      </ProposalCardShell>
    );
  }

  return (
    <ProposalCardShell
      kicker="Routine deletion"
      title={current.name ?? 'Routine'}
      subtitle={proposal.rationale}
      accentClass="bg-destructive"
      actions={
        <>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            disabled={deleteMutation.isPending}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Keep it
          </button>
          <button
            type="button"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {deleteMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
            Delete routine
          </button>
        </>
      }
    >
      <div className="rounded-lg bg-muted dark:bg-white/[0.02] border border-border p-3 space-y-2 text-sm">
        {current.schedule && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="w-3.5 h-3.5" />
            <span>{humanReadableCron(current.schedule)}</span>
            {current.timezone && <span className="text-xs">· {current.timezone}</span>}
          </div>
        )}
        {current.message && (
          <div className="text-foreground whitespace-pre-wrap font-mono text-xs leading-relaxed line-clamp-4">
            {current.message}
          </div>
        )}
      </div>
    </ProposalCardShell>
  );
};

export default RoutineDeletionProposalCard;
